import { ShipmentBranchSelect } from '@/views/screens/Shipment';
import { CountrySelect, CurrencySelect, StateSelect } from '@/views/screens/common';
import { Col, Form, Row } from 'antd';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE } from '../../../../../../contants/ClaimsEnums';
import { USER_PREFERENCES, YES_NO_OPTIONS } from '../../../../../../contants/common';
import { setWarningModal } from '../../../../../../redux/common/CommonActions';
import useUserPreference from '../../../../../../redux/hooks/useUserPreference';
import { formateWithMoment } from '../../../../../../utilz/dates';
import FormDatePicker from '../../../../../components/Inputs/FormDatePicker';
import FormInput from '../../../../../components/Inputs/FormInput';
import FormSelectBox from '../../../../../components/Inputs/FormSelectBox';
import FormTextArea from '../../../../../components/Inputs/FormTextArea';
import HiddenFormInput from '../../../../../components/Inputs/HiddenFormInput';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import TransportationProviderSelector from '../../../../../components/Selectors/AsynchSearch/TransportationProviderSelector';
import LossAndDamageClaimStatusSelector from '../../../../../components/Selectors/LossAndDamageClaimStatusSelector';
import PostalCodeInput from '../../../../../components/Selectors/PostalCodeInput';
import PreClaimsReasonSelector from '../../../../../components/Selectors/PreClaimsReasonSelector';
import PreClaimsReasonTypeSelector from '../../../../../components/Selectors/PreClaimsReasonTypeSelector';
import PreClaimsShipperConsigneeShortName from '../../../../../components/Selectors/PreClaimsShipperConsigneeShortName';
import PreClaimsTypeSelector from '../../../../../components/Selectors/PreClaimsTypeSelector';
import TMSFiledCard from '../../../../common/components/TMSFiledCard';
import { useGetLADClaimInitialValues } from '../../../hooks';

const LADClaimDetailsForm = ({ editMode, form, preparedBy, checkInstantClaimsDuplicate }) => {
  const dispatch = useDispatch();
  const { selectedCompany } = useSelector((state) => state.auth);
  const { data: initialCreateData } = useGetLADClaimInitialValues(selectedCompany?.companyId);
  const consigneeKey = Form.useWatch('consigneeKey', form);
  const consigneeCountry = Form.useWatch('consigneeCountry', form);
  const originKey = Form.useWatch('originKey', form);
  const shipperCountry = Form.useWatch('shipperCountry', form);
  const dateShipped = Form.useWatch('dateShipped', form);
  const invoiceDate = Form.useWatch('invoiceDate', form);
  const apDate = Form.useWatch('apDate', form);
  const claimCloseDate = Form.useWatch('claimCloseDate', form);
  const checkDateToCustomer = Form.useWatch('checkDateToCustomer', form);
  const dateFiled = Form.useWatch('dateFiled', form);
  const dateAck = Form.useWatch('dateAck', form);
  const carrierName = Form.useWatch('carrierName', form);
  const carrierKey = Form.useWatch('carrierKey', form);

  const handleConsigneeFetch = (type, data) => {
    let consigneeData = {};
    consigneeData.countryCode = initialCreateData?.consigneeCountry;
    if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.SUCCESS) {
      consigneeData = data;
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.EMPTY) {
      dispatch(setWarningModal({ open: true, description: 'No data found' }));
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.CLEAR) {
      // empty
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.ERROR) {
      dispatch(
        setWarningModal({
          open: true,
          description: 'Error while fetching data',
        }),
      );
    }
    form.setFieldsValue({
      consigneeName: consigneeData?.consigneeShipperName,
      consigneeAddr1: consigneeData?.addr1,
      consigneeAddr2: consigneeData?.addr2,
      consigneeCountry: consigneeData?.countryCode,
      consigneeZip: consigneeData?.zip,
      consigneeState: consigneeData?.state,
      consigneeCity: consigneeData?.city,
      consigneeKey: consigneeData?.claimShipRecKey,
    });
  };

  const handleConsigneeZipEntry = (data) => {
    form.setFieldsValue({
      consigneeCountry: data?.countryCode,
      consigneeZip: data?.zip,
      consigneeState: data?.state,
      consigneeCity: data?.city,
    });
  };

  const handleShipperFetch = (type, data) => {
    let consigneeData = {};
    consigneeData.countryCode = initialCreateData?.shipperCountry;
    if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.SUCCESS) {
      consigneeData = data;
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.EMPTY) {
      dispatch(setWarningModal({ open: true, description: 'No data found' }));
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.CLEAR) {
      // empty
    } else if (type === CLAIMS_SHIPPER_CONSIGNEE_FETCH_ERROR_TYPE.ERROR) {
      dispatch(
        setWarningModal({
          open: true,
          description: 'Error while fetching data',
        }),
      );
    }
    form.setFieldsValue({
      shipperName: consigneeData?.consigneeShipperName,
      shipperAddr1: consigneeData?.addr1,
      shipperAddr2: consigneeData?.addr2,
      shipperCountry: consigneeData?.countryCode,
      shipperZip: consigneeData?.zip,
      shipperState: consigneeData?.state,
      shipperCity: consigneeData?.city,
      originKey: consigneeData?.claimShipRecKey,
    });
  };

  const handleShipperZipEntry = (data) => {
    form.setFieldsValue({
      shipperCountry: data?.countryCode,
      shipperZip: data?.zip,
      shipperState: data?.state,
      shipperCity: data?.city,
    });
  };

  const userDateFormat = useUserPreference(USER_PREFERENCES.DEFAULT_DATE_FORMAT);

  const onChangeClaimStatus = (data) => {
    if (data?.value === 'C') {
      form.setFieldsValue({ claimCloseDate: moment(new Date()) });
    } else {
      form.setFieldsValue({ claimCloseDate: undefined });
    }
  };

  return (
    <Row gutter={30}>
      <Col sm={6}>
        <CurrencySelect
          margin="mb-10"
          editMode={editMode}
          name="currencyCode"
          rules={[
            {
              required: true,
              message: 'Please select a Currency',
            },
          ]}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={1}
        />
        <TransportationProviderSelector
          editMode={editMode}
          margin="mb-10"
          form={form}
          showPopupFilter
          label="Transportation Provider"
          labelForTestId="transportation-provider-selector"
          name="carrierKey"
          inputValue={carrierKey}
          carrierName={carrierName}
          disableOpen
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={2}
          rules={[
            {
              required: true,
              message: 'Please select a Transportation Provider',
            },
          ]}
        />
        <PreClaimsShipperConsigneeShortName
          margin="mb-10"
          label="Consignee Short Name"
          name="consigneeShortName"
          editMode={editMode}
          onFetch={handleConsigneeFetch}
          maxLength={9}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={3}
        />
        <FormInput
          margin="mb-10"
          label="Consignee"
          name="consigneeName"
          editMode={editMode && !consigneeKey}
          rules={[
            {
              required: true,
              message: 'Please enter the Consignee Name',
            },
          ]}
          maxLength={30}
          tabIndex={editMode && !consigneeKey ? 4 : -1}
        />
        <FormTextArea
          margin="mb-10"
          label="Address"
          name="consigneeAddr1"
          editMode={editMode && !consigneeKey}
          rows={2}
          rules={[
            {
              required: true,
              message: 'Please enter the Address',
            },
          ]}
          maxLength={35}
          tabIndex={editMode && !consigneeKey ? 5 : -1}
        />
        <FormTextArea
          margin="mb-10"
          label="Address 1"
          name="consigneeAddr2"
          editMode={editMode && !consigneeKey}
          rows={2}
          maxLength={35}
          tabIndex={editMode && !consigneeKey ? 6 : -1}
        />
        <CountrySelect
          margin="mb-10"
          label="Country"
          name="consigneeCountry"
          editMode={editMode && !consigneeKey}
          rules={[
            {
              required: true,
              message: 'Please select a Country',
            },
          ]}
          tabIndex={editMode && !consigneeKey ? 7 : -1}
        />
        <PostalCodeInput
          margin="mb-10"
          label="Postal Code"
          name="consigneeZip"
          editMode={editMode && !consigneeKey}
          rules={[
            {
              required: true,
              message: 'Please enter the consignee Postal Code',
            },
          ]}
          countryCode={consigneeCountry}
          handleZipDetails={handleConsigneeZipEntry}
          maxLength={10}
          tabIndex={editMode && !consigneeKey ? 8 : -1}
        />
        <StateSelect
          margin="mb-10"
          label="State"
          name="consigneeState"
          editMode={false}
          rules={[
            {
              required: true,
              message: 'Please select a state',
            },
          ]}
          countryCode={consigneeCountry}
        />
        <FormInput
          margin="mb-10"
          label="City"
          name="consigneeCity"
          editMode={false}
          rules={[
            {
              required: true,
              message: 'Please enter city',
            },
          ]}
        />
        <PreClaimsTypeSelector
          editMode={editMode}
          name="claimTypeKey"
          margin="mb-10"
          dataTestId="pre-claim-type-selector"
          rules={[
            {
              required: true,
              message: 'Please select a claim Type',
            },
          ]}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={9}
        />
        {/* biome-ignore lint/a11y/noPositiveTabindex: <explanation> */}
        <FormInput margin="mb-10" label="Vehicle" name="vehicleNo" editMode={editMode} maxLength={20} tabIndex={10} />
      </Col>
      <Col sm={6}>
        <ShipmentBranchSelect
          editMode={editMode}
          label="Branch"
          name="branchKey"
          margin="mb-10"
          rules={[
            {
              required: true,
              message: 'Please select a Branch',
            },
          ]}
          customerId={selectedCompany.companyId}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={11}
        />
        <PreClaimsShipperConsigneeShortName
          margin="mb-10"
          label="Shipper Short Name"
          placeholder="Enter shipper short name"
          name="shipperShortName"
          dataTestId="shipper-short-name"
          editMode={editMode}
          onFetch={handleShipperFetch}
          maxLength={9}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={12}
        />
        <FormInput
          margin="mb-10"
          label="Shipper"
          name="shipperName"
          editMode={editMode && !originKey}
          rules={[
            {
              required: true,
              message: 'Please enter the Shipper Name',
            },
          ]}
          maxLength={30}
          tabIndex={editMode && !originKey ? 13 : -1}
        />
        <FormTextArea
          margin="mb-10"
          label="Address"
          name="shipperAddr1"
          editMode={editMode && !originKey}
          rows={2}
          rules={[
            {
              required: true,
              message: 'Please enter the Address',
            },
          ]}
          maxLength={35}
          tabIndex={editMode && !originKey ? 14 : -1}
        />
        <FormTextArea
          margin="mb-10"
          label="Address 1"
          name="shipperAddr2"
          editMode={editMode && !originKey}
          rows={2}
          maxLength={35}
          tabIndex={editMode && !originKey ? 15 : -1}
        />
        <CountrySelect
          margin="mb-10"
          label="Country"
          name="shipperCountry"
          editMode={editMode && !originKey}
          rules={[
            {
              required: true,
              message: 'Please select a Country',
            },
          ]}
          tabIndex={editMode && !originKey ? 16 : -1}
        />
        <PostalCodeInput
          margin="mb-10"
          label="Postal Code"
          name="shipperZip"
          editMode={editMode && !originKey}
          rules={[
            {
              required: true,
              message: 'Please enter the shipper Postal Code',
            },
          ]}
          countryCode={shipperCountry}
          handleZipDetails={handleShipperZipEntry}
          maxLength={10}
          tabIndex={editMode && !originKey ? 17 : -1}
        />
        <StateSelect
          margin="mb-10"
          label="State"
          name="shipperState"
          editMode={false}
          rules={[
            {
              required: true,
              message: 'Please select a state',
            },
          ]}
          countryCode={shipperCountry}
        />
        <FormInput
          margin="mb-10"
          label="City"
          name="shipperCity"
          editMode={false}
          rules={[
            {
              required: true,
              message: 'Please enter city',
            },
          ]}
        />
        <FormInput
          margin="mb-10"
          label="Invoice Number"
          name="invoiceNo"
          dataTestId="invoice-no"
          editMode={editMode}
          maxLength={25}
          rules={[
            {
              required: true,
              message: 'Please enter Invoice Number',
            },
          ]}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={18}
          onBlur={checkInstantClaimsDuplicate('invoiceNo')}
        />
        <FormInput
          margin="mb-10"
          label="Freight Bill / Pro Number"
          name="claimProNo"
          editMode={editMode}
          rules={[
            {
              required: true,
              message: 'Please enter Freight Bill / Pro Number',
            },
          ]}
          maxLength={25}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={19}
          onBlur={checkInstantClaimsDuplicate('claimProNo')}
        />
      </Col>
      <Col sm={6}>
        {editMode ? (
          <FormDatePicker
            margin="mb-10"
            label="Ship Date"
            name="dateShipped"
            editMode={editMode}
            rules={[
              {
                required: true,
                message: 'Please select Ship Date',
              },
            ]}
            disabledTargetDate={new Date(new Date().setDate(new Date().getDate() + 1))}
            disableFuture
            // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
            tabIndex={20}
          />
        ) : (
          <TMSFiledCard
            containerClass="mb-10"
            title={
              <span className="fw-700 f-14">
                Ship Date<span className="text-iconDanger f-16">*</span>
              </span>
            }
          >
            <PrimaryLabel>{formateWithMoment(dateShipped, userDateFormat)}</PrimaryLabel>
          </TMSFiledCard>
        )}
        {editMode ? (
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          <FormDatePicker margin="mb-10" label="Invoice Date" name="invoiceDate" editMode={editMode} tabIndex={21} />
        ) : (
          <TMSFiledCard containerClass="mb-10" title="Invoice Date">
            <PrimaryLabel>{invoiceDate ? formateWithMoment(invoiceDate, userDateFormat) : '-'}</PrimaryLabel>
          </TMSFiledCard>
        )}
        {editMode ? (
          <FormDatePicker
            margin="mb-10"
            label="Accounts Payable Date"
            name="apDate"
            editMode={editMode}
            // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
            tabIndex={22}
          />
        ) : (
          <TMSFiledCard containerClass="mb-10" title="Accounts Payable Date">
            <PrimaryLabel>{formateWithMoment(apDate, userDateFormat)}</PrimaryLabel>
          </TMSFiledCard>
        )}
        <FormInput
          margin="mb-30"
          label="Transportation Provider Claim Number"
          name="claimAckNo"
          editMode={editMode}
          maxLength={15}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={23}
        />
        <TMSFiledCard containerClass="mb-10" title="Prepared By">
          {preparedBy}
        </TMSFiledCard>
        <PreClaimsReasonTypeSelector
          editMode={editMode}
          name="reasonType"
          margin="mb-10"
          rules={[
            {
              required: true,
              message: 'Please select a claim Reason Type',
            },
          ]}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={24}
        />
        {editMode ? (
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          <FormDatePicker margin="mb-10" label="Closed" name="claimCloseDate" editMode={editMode} tabIndex={25} />
        ) : (
          <TMSFiledCard containerClass="mb-15 mt-15" title={<span className="f-12">Closed</span>}>
            <PrimaryLabel>{formateWithMoment(claimCloseDate, userDateFormat)}</PrimaryLabel>
          </TMSFiledCard>
        )}
        <FormInput
          margin="mb-10"
          label="Check Number from Transportation Provider"
          name="checkNoFromCarrier"
          editMode={editMode}
          dataTestId="check-no-from-carrier"
          maxLength={20}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={26}
        />
        <FormInput
          margin="mb-10"
          label="Check Number to Customer"
          dataTestId="check-no-to-customer"
          name="checkNoToCustomer"
          editMode={editMode}
          maxLength={20}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={27}
        />
        {editMode ? (
          <FormDatePicker
            margin="m-0"
            label="Date Refunded to Customer"
            name="checkDateToCustomer"
            editMode={editMode}
            // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
            tabIndex={28}
          />
        ) : (
          <TMSFiledCard containerClass="mb-10" title={<span className="fw-700 f-14">Date Refunded to Customer</span>}>
            <PrimaryLabel>{formateWithMoment(checkDateToCustomer, userDateFormat)}</PrimaryLabel>
          </TMSFiledCard>
        )}
      </Col>
      <Col sm={6}>
        <FormInput
          margin="mb-10"
          label="Accounts Payable"
          name="apNumber"
          editMode={editMode}
          maxLength={18}
          dataTestId="ap-number"
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={29}
        />
        {editMode ? (
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          <FormDatePicker margin="mb-10" label="Filed" name="dateFiled" editMode={editMode} tabIndex={30} />
        ) : (
          <TMSFiledCard containerClass="mb-10" title={<span className="fw-700 f-14">Filed</span>}>
            <PrimaryLabel>{formateWithMoment(dateFiled, userDateFormat)}</PrimaryLabel>
          </TMSFiledCard>
        )}
        {editMode ? (
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          <FormDatePicker margin="mb-10" label="Date Acknowledged" name="dateAck" editMode={editMode} tabIndex={31} />
        ) : (
          <TMSFiledCard containerClass="mb-10" title={<span className="fw-700 f-14">Date Acknowledged</span>}>
            <PrimaryLabel>{formateWithMoment(dateAck, userDateFormat)}</PrimaryLabel>
          </TMSFiledCard>
        )}
        <LossAndDamageClaimStatusSelector
          margin="mb-10"
          label="Status"
          name="claimStatusCode"
          editMode={editMode}
          rules={[
            {
              required: true,
              message: 'Please select a claim Status',
            },
          ]}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={32}
          onChange={onChangeClaimStatus}
        />
        <PreClaimsReasonSelector
          editMode={editMode}
          name="reasonCode"
          margin="mb-10"
          rules={[
            {
              required: true,
              message: 'Please select a claim Reason',
            },
          ]}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={33}
        />
        <FormInput
          inputType="number"
          margin="mb-10"
          label="Amount Paid"
          name="claimPaidAmount"
          editMode={editMode}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={34}
        />
        <FormInput
          margin="mb-10"
          label="Extension"
          name="claimExtension"
          editMode={editMode}
          maxLength={10}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={35}
        />
        <FormInput
          margin="mb-10"
          label="Accounting Code"
          name="acctCode"
          editMode={editMode}
          maxLength={26}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={36}
        />
        <FormInput
          margin="mb-10"
          label="Seal Number"
          name="sealNumber"
          editMode={editMode}
          maxLength={50}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={37}
        />
        <FormSelectBox
          margin="mb-10"
          label="Seal Intact"
          placeholder="Select Seal Intact"
          name="sealIntact"
          editMode={editMode}
          maxLength={1}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={38}
          options={YES_NO_OPTIONS}
          showSearch={false}
          shouldUseNativeSelect
        />
      </Col>
      <HiddenFormInput name="dateShipped" />
      <HiddenFormInput name="invoiceDate" />
      <HiddenFormInput name="apDate" />
      <HiddenFormInput name="claimCloseDate" />
      <HiddenFormInput name="checkDateToCustomer" />
      <HiddenFormInput name="dateFiled" />
      <HiddenFormInput name="dateAck" />
      <HiddenFormInput name="carrierName" />
      <HiddenFormInput name="consigneeKey" />
      <HiddenFormInput name="originKey" />
    </Row>
  );
};

export default LADClaimDetailsForm;
